import { company } from '~/company/Company';

import type { AnalyticsProvider } from '../types';

interface DataLayerWindow extends Window {
  dataLayer?: Array<Record<string, unknown>>;
}

const SENT_PURCHASES_STORAGE_KEY = 'yaMetrikaSentPurchases';
const SENT_PURCHASES_LIMIT = 20;

export const YM_HIT_EVENT = 'ym:hit';

const isSamePage = (url: string, other: string | undefined): boolean => {
  if (!other) return false;
  try {
    return new URL(url).pathname === new URL(other).pathname;
  } catch {
    return url === other;
  }
};

export class YandexMetrikaProvider implements AnalyticsProvider {
  readonly name = 'yandexMetrika' as const;
  readonly requiresConsent = true;

  private entryHitChecked = false;
  private lastHitUrl: string | null = null;

  // The id the tag was initialised with wins: the server picks it from
  // NODE_ENV, the bundle only knows the build-time REACT_APP_ENV, and where the
  // two disagree the config's id belongs to a counter no tag initialised.
  private get counterId(): number | undefined {
    const yaMetrika = company.config.apis?.yaMetrika;
    const injected =
      typeof window === 'undefined' ? undefined : window.__YM_ID__;
    const id = Number(
      injected ??
        (import.meta.env.REACT_APP_ENV === 'production'
          ? yaMetrika?.production
          : yaMetrika?.test),
    );
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }

  private get isEcommerceEnabled(): boolean {
    return company.config.apis?.yaMetrika?.useEcommercePayload === true;
  }

  isAvailable(): boolean {
    if (typeof window === 'undefined') return false;
    return !!this.counterId;
  }

  initialize(): void {
    // Yandex Metrika is loaded via server-injected script, no initialization needed
  }

  track(eventName: string, payload: Record<string, unknown>): void {
    if (payload.ecommerce) {
      this.trackEcommerce(payload);
      return;
    }

    if (eventName === YM_HIT_EVENT) {
      this.trackPageView(payload);
      return;
    }

    if (!window.ym || !this.counterId) return;
    window.ym(this.counterId, 'reachGoal', eventName, payload);
  }

  private trackPageView(payload: Record<string, unknown>): void {
    const counterId = this.counterId;
    const url = payload.url;
    if (!window.ym || !counterId || typeof url !== 'string') return;

    // `init` already counted one page. Matched by value, not by call count: the
    // first call to arrive is not always the entry page, since the visitor may
    // accept the cookie banner mid-visit.
    if (!this.entryHitChecked) {
      this.entryHitChecked = true;
      if (isSamePage(url, window.__YM_ENTRY_URL__)) {
        this.lastHitUrl = url;
        return;
      }
    }

    if (url === this.lastHitUrl) return;

    window.ym(
      counterId,
      'hit',
      url,
      this.lastHitUrl ? { referer: this.lastHitUrl } : undefined,
    );
    this.lastHitUrl = url;
  }

  private trackEcommerce(payload: Record<string, unknown>): void {
    if (!this.isEcommerceEnabled) return;

    const ecommerce = payload.ecommerce as Record<string, unknown>;
    const purchase = ecommerce.purchase as
      | { actionField?: { id?: string } }
      | undefined;
    const orderId = purchase?.actionField?.id;

    if (orderId) {
      if (this.isPurchaseAlreadySent(orderId)) return;
      this.rememberSentPurchase(orderId);
    }

    const w = window as DataLayerWindow;
    if (!w.dataLayer) {
      w.dataLayer = [];
    }
    w.dataLayer.push(payload);
  }

  private isPurchaseAlreadySent(orderId: string): boolean {
    return this.readSentPurchases().includes(orderId);
  }

  private rememberSentPurchase(orderId: string): void {
    const sent = [...this.readSentPurchases(), orderId].slice(
      -SENT_PURCHASES_LIMIT,
    );

    try {
      localStorage.setItem(SENT_PURCHASES_STORAGE_KEY, JSON.stringify(sent));
    } catch {
      return;
    }
  }

  private readSentPurchases(): string[] {
    try {
      const raw = localStorage.getItem(SENT_PURCHASES_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.filter((id): id is string => typeof id === 'string')
        : [];
    } catch {
      return [];
    }
  }
}
